import React from 'react';
import { Paper, useTheme } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import Plot from 'react-plotly.js';
import { REITCompareResponse, ComparisonDataPoint, RatioDataPoint, DiffDataPoint, DividendDataPoint } from '../types';
import { PriceMethod } from './Dashboard';

interface ComparisonChartsProps {
  data: REITCompareResponse;
  priceMethod: PriceMethod;
}

const TICKER1_COLOR = '#4ecdc4';
const TICKER2_COLOR = '#ff6b6b';
const SPREAD_COLOR = '#fbbf24';

export default function ComparisonCharts({ data, priceMethod }: ComparisonChartsProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Use explicit colors like bonds dashboard
  const bgColor = isDarkMode ? '#1e293b' : '#ffffff';
  const gridColor = isDarkMode ? '#334155' : '#e2e8f0';
  const textColor = isDarkMode ? '#94a3b8' : '#64748b';
  // More contrasty spike color for better visibility
  const spikeColor = isDarkMode ? '#94a3b8' : '#475569';

  const chartLayout = (title: string, yAxisTitle: string): any => ({
    title: {
      text: title,
      font: { size: 14, color: textColor },
    },
    paper_bgcolor: bgColor,
    plot_bgcolor: bgColor,
    font: { color: textColor },
    margin: { l: 50, r: 30, t: 40, b: 40 },
    xaxis: {
      showgrid: true,
      gridcolor: gridColor,
      gridwidth: 1,
      griddash: 'dot',
      linecolor: gridColor,
      zeroline: false,
      showspikes: true,
      spikemode: 'across+toaxis',
      spikesnap: 'cursor',
      spikethickness: 1.5,
      spikecolor: spikeColor,
      spikedash: 'dot',
      spikedistance: -1,
    },
    yaxis: {
      title: { text: yAxisTitle },
      showgrid: true,
      gridcolor: gridColor,
      gridwidth: 1,
      griddash: 'dot',
      linecolor: gridColor,
      zeroline: false,
      showspikes: true,
      spikemode: 'across+toaxis',
      spikesnap: 'cursor',
      spikethickness: 1.5,
      spikecolor: spikeColor,
      spikedash: 'dot',
      spikedistance: -1,
    },
    legend: {
      orientation: 'h',
      y: -0.15,
      x: 0.5,
      xanchor: 'center',
    },
    hovermode: 'closest',
  });

  const chartConfig: any = {
    responsive: true,
    displayModeBar: false,
  };

  // Filter helpers
  const filterTicker = (arr: ComparisonDataPoint[], key: 'ticker1' | 'ticker2') =>
    arr.filter((d) => d[key] != null).map((d) => ({ x: d.date, y: d[key] }));

  const filterRatio = (arr: RatioDataPoint[]) =>
    arr.filter((d) => d.ratio != null).map((d) => ({ x: d.date, y: d.ratio }));

  const filterDiff = (arr: DiffDataPoint[]) =>
    arr.filter((d) => d.diff != null).map((d) => ({ x: d.date, y: d.diff }));

  // Reference line shape for ratio charts (at y=1) and diff charts (at y=0)
  const oneLineShape = {
    type: 'line',
    x0: 0,
    x1: 1,
    xref: 'paper',
    y0: 1,
    y1: 1,
    line: { color: 'rgba(148, 163, 184, 0.5)', width: 1, dash: 'dot' },
  };

  const zeroLineShape = {
    type: 'line',
    x0: 0,
    x1: 1,
    xref: 'paper',
    y0: 0,
    y1: 0,
    line: { color: 'rgba(148, 163, 184, 0.5)', width: 1, dash: 'dot' },
  };

  return (
    <Grid container spacing={3}>
      {/* Row 1: Prices (both tickers) and Price Spread */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 380 }}>
          <Plot
            data={[
              {
                x: filterTicker(data.prices, 'ticker1').map((d) => d.x),
                y: filterTicker(data.prices, 'ticker1').map((d) => d.y),
                type: 'scatter',
                mode: 'lines',
                name: data.ticker1,
                line: { color: TICKER1_COLOR, width: 2 },
                hovertemplate: '%{x|%b %d, %Y}<br>$%{y:.2f}<extra>%{fullData.name}</extra>',
              },
              {
                x: filterTicker(data.prices, 'ticker2').map((d) => d.x),
                y: filterTicker(data.prices, 'ticker2').map((d) => d.y),
                type: 'scatter',
                mode: 'lines',
                name: data.ticker2,
                line: { color: TICKER2_COLOR, width: 2 },
                hovertemplate: '%{x|%b %d, %Y}<br>$%{y:.2f}<extra>%{fullData.name}</extra>',
              },
            ]}
            layout={chartLayout('Prices', 'Price ($)')}
            config={chartConfig}
            style={{ width: '100%', height: '100%' }}
          />
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 380 }}>
          <Plot
            data={[
              {
                x: filterRatio(data.priceRatio).map((d) => d.x),
                y: filterRatio(data.priceRatio).map((d) => d.y),
                type: 'scatter',
                mode: 'lines',
                name: 'Price Ratio',
                line: { color: SPREAD_COLOR, width: 2 },
                hovertemplate: '%{x|%b %d, %Y}<br>%{y:.3f}x<extra></extra>',
              },
            ]}
            layout={{
              ...chartLayout(`Price Ratio (${data.ticker1} / ${data.ticker2})`, 'Ratio'),
              shapes: [oneLineShape],
            }}
            config={chartConfig}
            style={{ width: '100%', height: '100%' }}
          />
        </Paper>
      </Grid>

      {/* Row 2: Book Values (both tickers) and Book Value Ratio */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 380 }}>
          <Plot
            data={[
              {
                x: filterTicker(data.bookValues, 'ticker1').map((d) => d.x),
                y: filterTicker(data.bookValues, 'ticker1').map((d) => d.y),
                type: 'scatter',
                mode: 'lines',
                name: data.ticker1,
                line: { color: TICKER1_COLOR, width: 2, shape: 'hv' },
                hovertemplate: '%{x|%b %d, %Y}<br>$%{y:.2f}<extra>%{fullData.name}</extra>',
              },
              {
                x: filterTicker(data.bookValues, 'ticker2').map((d) => d.x),
                y: filterTicker(data.bookValues, 'ticker2').map((d) => d.y),
                type: 'scatter',
                mode: 'lines',
                name: data.ticker2,
                line: { color: TICKER2_COLOR, width: 2, shape: 'hv' },
                hovertemplate: '%{x|%b %d, %Y}<br>$%{y:.2f}<extra>%{fullData.name}</extra>',
              },
            ]}
            layout={chartLayout('Book Values', 'Book Value ($)')}
            config={chartConfig}
            style={{ width: '100%', height: '100%' }}
          />
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 380 }}>
          <Plot
            data={[
              {
                x: filterRatio(data.bookValueRatio).map((d) => d.x),
                y: filterRatio(data.bookValueRatio).map((d) => d.y),
                type: 'scatter',
                mode: 'lines',
                name: 'Book Value Ratio',
                line: { color: SPREAD_COLOR, width: 2 },
                hovertemplate: '%{x|%b %d, %Y}<br>%{y:.3f}x<extra></extra>',
              },
            ]}
            layout={{
              ...chartLayout(`Book Value Ratio (${data.ticker1} / ${data.ticker2})`, 'Ratio'),
              shapes: [oneLineShape],
            }}
            config={chartConfig}
            style={{ width: '100%', height: '100%' }}
          />
        </Paper>
      </Grid>

      {/* Row 3: P/B Ratios (both tickers) and P/B Difference */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 380 }}>
          <Plot
            data={[
              {
                x: filterRatio(data.priceBookRatio1).map((d) => d.x),
                y: filterRatio(data.priceBookRatio1).map((d) => d.y),
                type: 'scatter',
                mode: 'lines',
                name: data.ticker1,
                line: { color: TICKER1_COLOR, width: 2 },
                hovertemplate: '%{x|%b %d, %Y}<br>%{y:.3f}x<extra>%{fullData.name}</extra>',
              },
              {
                x: filterRatio(data.priceBookRatio2).map((d) => d.x),
                y: filterRatio(data.priceBookRatio2).map((d) => d.y),
                type: 'scatter',
                mode: 'lines',
                name: data.ticker2,
                line: { color: TICKER2_COLOR, width: 2 },
                hovertemplate: '%{x|%b %d, %Y}<br>%{y:.3f}x<extra>%{fullData.name}</extra>',
              },
            ]}
            layout={{
              ...chartLayout('Price / Book Ratio', 'P/B Ratio'),
              shapes: [{ ...zeroLineShape, y0: 1, y1: 1 }],
            }}
            config={chartConfig}
            style={{ width: '100%', height: '100%' }}
          />
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 380 }}>
          <Plot
            data={[
              {
                x: filterDiff(data.priceBookDiff).map((d) => d.x),
                y: filterDiff(data.priceBookDiff).map((d) => d.y),
                type: 'scatter',
                mode: 'lines',
                name: 'P/B Difference',
                fill: 'tozeroy',
                line: { color: SPREAD_COLOR, width: 2 },
                fillcolor: 'rgba(251, 191, 36, 0.2)',
                hovertemplate: '%{x|%b %d, %Y}<br>%{y:.3f}<extra></extra>',
              },
            ]}
            layout={{
              ...chartLayout(`P/B Difference (${data.ticker1} - ${data.ticker2})`, 'Difference'),
              shapes: [zeroLineShape],
            }}
            config={chartConfig}
            style={{ width: '100%', height: '100%' }}
          />
        </Paper>
      </Grid>

      {/* Row 4: Dividends (both tickers) and Dividend Spread */}
      {(() => {
        // Build dividend spread with carry-forward logic
        const div1Map: Record<string, number> = {};
        const div2Map: Record<string, number> = {};
        data.dividends1?.forEach(d => { div1Map[d.date] = d.amount; });
        data.dividends2?.forEach(d => { div2Map[d.date] = d.amount; });

        const allDates = Array.from(new Set([
          ...(data.dividends1?.map(d => d.date) || []),
          ...(data.dividends2?.map(d => d.date) || [])
        ])).sort();

        // Carry forward last known dividend for each ticker
        let lastDiv1 = 0;
        let lastDiv2 = 0;
        const spreadData = allDates.map(date => {
          if (div1Map[date] !== undefined) lastDiv1 = div1Map[date];
          if (div2Map[date] !== undefined) lastDiv2 = div2Map[date];
          return { date, spread: lastDiv1 - lastDiv2 };
        });

        // Build accrual data for plotting
        const accrual1Data = data.accruals1?.filter(d => d.ticker1 != null) || [];
        const accrual2Data = data.accruals2?.filter(d => d.ticker2 != null) || [];

        // Build accrual spread
        const acc1Map: Record<string, number> = {};
        const acc2Map: Record<string, number> = {};
        accrual1Data.forEach(d => { acc1Map[d.date] = d.ticker1 ?? 0; });
        accrual2Data.forEach(d => { acc2Map[d.date] = d.ticker2 ?? 0; });

        const allAccrualDates = Array.from(new Set([
          ...Object.keys(acc1Map),
          ...Object.keys(acc2Map)
        ])).sort();

        const accrualSpreadData = allAccrualDates.map(date => ({
          date,
          spread: (acc1Map[date] ?? 0) - (acc2Map[date] ?? 0)
        }));

        // Dividend chart traces - always include dividends
        const dividendTraces: any[] = [
          {
            x: data.dividends1?.map((d: DividendDataPoint) => d.date) || [],
            y: data.dividends1?.map((d: DividendDataPoint) => d.amount) || [],
            type: 'scatter' as const,
            mode: 'lines' as const,
            name: `${data.ticker1} Div`,
            line: { color: TICKER1_COLOR, width: 2, shape: 'hv' as const },
            hovertemplate: '%{x|%b %d, %Y}<br>$%{y:.4f}<extra>%{fullData.name}</extra>',
          },
          {
            x: data.dividends2?.map((d: DividendDataPoint) => d.date) || [],
            y: data.dividends2?.map((d: DividendDataPoint) => d.amount) || [],
            type: 'scatter' as const,
            mode: 'lines' as const,
            name: `${data.ticker2} Div`,
            line: { color: TICKER2_COLOR, width: 2, shape: 'hv' as const },
            hovertemplate: '%{x|%b %d, %Y}<br>$%{y:.4f}<extra>%{fullData.name}</extra>',
          },
        ];

        // Add accrual % lines (dotted, same color as dividend) - shown as % of dividend
        if (accrual1Data.length > 0) {
          dividendTraces.push({
            x: accrual1Data.map(d => d.date),
            y: accrual1Data.map(d => d.ticker1),
            type: 'scatter' as const,
            mode: 'lines' as const,
            name: `${data.ticker1} Accrual %`,
            line: { color: TICKER1_COLOR, width: 2, dash: 'dot' as const },
            yaxis: 'y2',
            hovertemplate: '%{x|%b %d, %Y}<br>%{y:.1f}%<extra>%{fullData.name}</extra>',
          });
        }
        if (accrual2Data.length > 0) {
          dividendTraces.push({
            x: accrual2Data.map(d => d.date),
            y: accrual2Data.map(d => d.ticker2),
            type: 'scatter' as const,
            mode: 'lines' as const,
            name: `${data.ticker2} Accrual %`,
            line: { color: TICKER2_COLOR, width: 2, dash: 'dot' as const },
            yaxis: 'y2',
            hovertemplate: '%{x|%b %d, %Y}<br>%{y:.1f}%<extra>%{fullData.name}</extra>',
          });
        }

        // Dividend spread chart traces
        const spreadTraces: any[] = [
          {
            x: spreadData.map(d => d.date),
            y: spreadData.map(d => d.spread),
            type: 'scatter' as const,
            mode: 'lines' as const,
            name: 'Div Spread',
            fill: 'tozeroy' as const,
            line: { color: SPREAD_COLOR, width: 2, shape: 'hv' as const },
            fillcolor: 'rgba(251, 191, 36, 0.2)',
            hovertemplate: '%{x|%b %d, %Y}<br>$%{y:.4f}<extra></extra>',
          },
        ];

        // Add accrual % spread line (dotted, same color as dividend spread)
        if (accrualSpreadData.length > 0) {
          spreadTraces.push({
            x: accrualSpreadData.map(d => d.date),
            y: accrualSpreadData.map(d => d.spread),
            type: 'scatter' as const,
            mode: 'lines' as const,
            name: 'Accrual % Diff',
            line: { color: SPREAD_COLOR, width: 2, dash: 'dot' as const },
            yaxis: 'y2',
            hovertemplate: '%{x|%b %d, %Y}<br>%{y:.1f}%<extra></extra>',
          });
        }

        return (
          <>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: 380 }}>
                <Plot
                  data={dividendTraces}
                  layout={{
                    ...chartLayout('Dividends & Accrual %', 'Dividend ($)'),
                    yaxis: {
                      ...chartLayout('', '').yaxis,
                      title: { text: 'Dividend ($)' },
                      rangemode: 'tozero',
                    },
                    yaxis2: {
                      title: { text: 'Accrual (%)' },
                      overlaying: 'y',
                      side: 'right',
                      showgrid: false,
                      range: [0, 110],
                      ticksuffix: '%',
                    },
                  }}
                  config={chartConfig}
                  style={{ width: '100%', height: '100%' }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: 380 }}>
                <Plot
                  data={spreadTraces}
                  layout={{
                    ...chartLayout(`Dividend Spread & Accrual % Diff`, 'Div Spread ($)'),
                    shapes: [zeroLineShape],
                    yaxis2: {
                      title: { text: 'Accrual Diff (%)' },
                      overlaying: 'y',
                      side: 'right',
                      showgrid: false,
                      ticksuffix: '%',
                    },
                  }}
                  config={chartConfig}
                  style={{ width: '100%', height: '100%' }}
                />
              </Paper>
            </Grid>
          </>
        );
      })()}
    </Grid>
  );
}
